<script setup lang="ts">
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { format as formatDate } from "date-fns";
import { twMerge } from "tailwind-merge";
import { computed, ref } from "vue";

import { useReceipts } from "@/composables/api/useReceipts";
import { useUser } from "@/composables/api/useUser";

import ReceiptBadge from "@/components/sections/receipts-list/ReceiptBadge.vue";
import ReceiptMonthFilter from "@/components/sections/receipts-list/ReceiptMonthFilter.vue";

const { data: user } = useUser();

const STAGGER_DELAY = 150;
const BASE_DURATION = 500;

const activeMonth = ref<string | null>(null);
const activeYear = ref<string | null>(null);

const { data: receipts } = useReceipts();

const filteredReceipts = computed(() => {
  if ((!activeMonth.value && !activeYear.value) || !receipts.value) {
    return receipts.value;
  }

  return receipts.value.filter((receipt) => {
    const date = new Date(receipt.created);
    const month = formatDate(date, "MMMM").toLowerCase();
    const year = formatDate(date, "yyyy");
    const matchMonth = activeMonth.value ? month === activeMonth.value : true;
    const matchYear = activeYear.value ? year === activeYear.value : true;
    return matchMonth && matchYear;
  });
});

const hasNoResults = computed(() => {
  return receipts.value?.length === 0 && filteredReceipts.value?.length === 0;
});

function onBeforeEnter(el: HTMLElement) {
  el.style.opacity = "0";
  el.style.transform = "translateY(32px)";
}

function onEnter(el: HTMLElement, done: () => void) {
  const delay = (el.dataset.index as unknown as number) * STAGGER_DELAY;

  setTimeout(() => {
    el.style.transition = `all ${BASE_DURATION}ms ease-out`;
    el.style.opacity = "1";
    el.style.transform = "translateY(0)";
  }, delay);

  setTimeout(done, delay + BASE_DURATION);
}
</script>

<template>
  <section
    :class="
      twMerge('bg-galbani-light', user?.current_mechanic === 'lactel' && 'bg-lactel-blue-light')
    "
  >
    <div class="mx-auto flex max-w-xl flex-col items-center gap-5 px-10 py-8">
      <h3
        :class="
          twMerge(
            'text-galbani text-center text-base leading-[130%] font-semibold lg:text-xl lg:tracking-tight',
            user?.current_mechanic === 'lactel' && 'text-lactel-blue',
          )
        "
      >
        {{ $t("receipts.title") }}
      </h3>

      <ReceiptMonthFilter
        v-model:month="activeMonth"
        v-model:year="activeYear"
        :hasActiveFilter="!!activeMonth"
      />

      <!-- Empty State -->
      <div
        v-if="hasNoResults"
        class="flex w-full flex-col items-center gap-4 rounded-[20px] bg-white p-8 text-center shadow-xl"
      >
        <div class="flex size-12 items-center justify-center rounded-full bg-gray-100">
          <XMarkIcon class="size-6 text-gray-400" />
        </div>

        <div class="space-y-1">
          <h4 class="text-primary font-semibold">
            {{ $t("receipts.emptyState.title") }}
          </h4>

          <p class="text-secondary text-base">
            {{ $t("receipts.emptyState.description") }}
          </p>
        </div>
      </div>

      <!-- Receipt List -->
      <TransitionGroup
        tag="ul"
        class="flex w-full flex-col gap-5"
        :css="false"
        role="list"
        aria-label="Liste des tickets de caisse"
        @beforeEnter="onBeforeEnter($event as HTMLElement)"
        @enter="(el, done) => onEnter(el as HTMLElement, done)"
      >
        <li
          v-for="(receipt, index) in filteredReceipts"
          :key="receipt.id"
          :data-index="index"
          :class="
            twMerge(
              'relative rounded-[20px] bg-white shadow-xl',
              receipt.loyalty_points_boost && receipt.loyalty_points_boost > 0
                ? twMerge(
                    'overflow-hidden border-[6px] border-[#FEB927]',
                    user?.current_mechanic === 'lactel' && 'border-lactel-yellow',
                  )
                : 'p-5',
            )
          "
        >
          <RouterLink
            :to="{ name: 'receipt', params: { id: receipt.id } }"
            class="flex w-full flex-col items-center gap-3"
          >
            <!-- Boost Tag (top, non-lactel only) -->
            <div
              v-if="
                user?.current_mechanic !== 'lactel' &&
                receipt.status !== 'declared' &&
                receipt.loyalty_points_boost &&
                receipt.loyalty_points_boost > 0
              "
              class="flex w-full justify-center"
            >
              <div
                class="flex items-center justify-center gap-2.5 rounded-b-[8px] bg-[#FEB927] px-4 py-2 shadow-[0px_0px_2px_0px_rgba(0,0,0,0.15)]"
              >
                <p class="text-galbani text-center text-base leading-5 font-bold whitespace-nowrap">
                  {{ $t("receipt.bonusTag", { boost: receipt.loyalty_points_boost }) }}
                </p>
              </div>
            </div>

            <div class="text-primary text-center text-sm font-semibold">
              <p>{{ $t("receipt.sentAt") }}</p>

              <time :datetime="receipt.created" class="block text-center text-xl">
                {{ formatDate(receipt.created, "dd.MM.yy") }}
              </time>
            </div>

            <div
              class="relative flex h-[96px] w-[109px] items-center justify-center overflow-hidden"
              aria-label="Points gagnés"
            >
              <svg
                aria-hidden="true"
                class="pointer-events-none absolute inset-0 h-auto w-full object-contain"
                xmlns="http://www.w3.org/2000/svg"
                width="110"
                height="96"
                viewBox="0 0 110 96"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M109 14.0408V81.8572V90.9998V91.1791L97.3129 95.898L86.748 91.1791L76.001 95.898L65.4361 91.1791L54.6891 95.898L44.1242 91.1791L33.3772 95.898L22.8124 91.1791L12.0654 95.898L1.5005 91.1791L1.5005 90.9998H1V4.9998H1.50049L1.5005 4.71891L12.0654 0L22.8124 4.71891L33.3772 0L44.1242 4.71891L54.6891 0L65.4361 4.71891L76.001 0L86.748 4.71891L97.3129 0L109 4.9998V14.0408Z"
                  :fill="user?.current_mechanic === 'lactel' ? '#1083C6' : '#214C9F'"
                />
              </svg>

              <div class="relative flex flex-col items-center gap-0.5 text-white">
                <span class="text-center text-[30px] leading-none font-bold">
                  {{
                    receipt.status === "declared"
                      ? "?"
                      : `${
                          receipt.loyalty_points_boost && receipt.loyalty_points_boost > 0
                            ? receipt.loyalty_points - receipt.loyalty_points_boost
                            : receipt.loyalty_points
                        }`
                  }}
                </span>

                <span class="text-center text-base">
                  {{
                    receipt.loyalty_points === 1 || receipt.loyalty_points === 0
                      ? "point"
                      : "points"
                  }}
                </span>
              </div>
            </div>

            <ReceiptBadge :status="receipt.status || 'declared'" />

            <!-- Boost Tag (bottom for lactel) -->
            <div
              v-if="
                user?.current_mechanic === 'lactel' &&
                receipt.loyalty_points_boost &&
                receipt.loyalty_points_boost > 0
              "
              class="flex w-full justify-center"
            >
              <div
                class="border-lactel-yellow bg-lactel-yellow flex items-center justify-center rounded-t-[12px] border-t-[6px] px-4 pt-2.5 pb-2"
              >
                <p
                  class="text-lactel-blue text-center text-base leading-5 font-bold whitespace-nowrap"
                >
                  {{ $t("receipt.bonusTag", { boost: receipt.loyalty_points_boost }) }}
                </p>
              </div>
            </div>
          </RouterLink>
        </li>
      </TransitionGroup>
    </div>
  </section>
</template>
